import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import useGetQueryFn from '../use-get-query-fn';
import type {
  AdminStore,
  AdminStoreListParams,
  AdminStoreListResponse,
  AdminStoreSummary,
  StoreAnalytics,
  StoreAuditEntry,
  StorePayout,
  StorePayoutConfig,
  StorePlanResponse,
  StoreSettingsRow,
  StoreListingsResponse,
  StoreStaffResponse,
} from '@/client/types/stores';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const BASE = '/api/admin/stores';

export const adminStoreKeys = {
  all: ['admin', 'stores'] as const,
  lists: () => [...adminStoreKeys.all, 'list'] as const,
  list: (params: AdminStoreListParams) => [...adminStoreKeys.lists(), params] as const,
  detail: (storeid: number | null) => [...adminStoreKeys.all, 'detail', storeid] as const,
  plan: (storeid: number | null) => [...adminStoreKeys.all, 'plan', storeid] as const,
  payoutConfig: (storeid: number | null) => [...adminStoreKeys.all, 'payout-config', storeid] as const,
  payouts: (storeid: number | null) => [...adminStoreKeys.all, 'payouts', storeid] as const,
  settings: (storeid: number | null) => [...adminStoreKeys.all, 'settings', storeid] as const,
  audit: (storeid: number | null) => [...adminStoreKeys.all, 'audit', storeid] as const,
  analytics: (storeid: number | null) => [...adminStoreKeys.all, 'analytics', storeid] as const,
  listings: (storeid: number | null, params: StorePageParams = {}) =>
    [...adminStoreKeys.all, 'listings', storeid, params] as const,
  staff: (storeid: number | null) => [...adminStoreKeys.all, 'staff', storeid] as const,
};

/**
 * Every per-store key is `[admin, stores, <resource>, storeid, …]`, so a write
 * can refresh all of one store's views with a single predicate.
 */
export const isStoreScopedKey = (key: readonly unknown[], storeid: number): boolean =>
  key[0] === 'admin' && key[1] === 'stores' && (key[2] === 'list' || key[3] === storeid);

export interface StorePageParams {
  limit?: number;
  offset?: number;
  status?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
}

/** The proxy returns `{ status, data: <payload> }`; unwrap to the payload. */
export const unwrap = <T,>(raw: any, fallback: T): T => (raw?.data ?? raw ?? fallback) as T;

export const useStoresQuery = (params: AdminStoreListParams = {}) => {
  const queryFn = useGetQueryFn({ endpoint: BASE, defaultErrorMessage: 'Failed to load stores' });
  return useQuery({
    queryKey: adminStoreKeys.list(params),
    queryFn: async (): Promise<AdminStoreListResponse> =>
      unwrap(await queryFn({ ...params }), { stores: [], total_count: 0, limit: 0, offset: 0 }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Headline counts for the list's filter chips (same filters as the list, minus paging). */
export const useStoresSummaryQuery = (params: AdminStoreListParams = {}) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/summary` });
  const { limit: _l, offset: _o, sort: _s, order: _ord, ...filters } = params;
  return useQuery({
    queryKey: [...adminStoreKeys.lists(), 'summary', filters] as const,
    queryFn: async (): Promise<AdminStoreSummary | null> =>
      unwrap<{ summary: AdminStoreSummary | null }>(await queryFn({ ...filters }), { summary: null }).summary,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useStoreQuery = (storeid: number | null, enabled = true) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}`, defaultErrorMessage: 'Failed to load store' });
  const has = storeid !== null && Number.isFinite(storeid);
  const query = useQuery({
    queryKey: adminStoreKeys.detail(storeid),
    queryFn: async (): Promise<AdminStore | null> => unwrap(await queryFn({}), null),
    enabled: has && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: (n, err) => (err as any)?.status !== 404 && n < 1,
  });
  return {
    ...query,
    data: query.data ?? null,
    notFound: query.isError && (query.error as any)?.status === 404,
  };
};

export const useStorePlanQuery = (storeid: number | null) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/plan` });
  return useQuery({
    queryKey: adminStoreKeys.plan(storeid),
    queryFn: async (): Promise<StorePlanResponse | null> => unwrap(await queryFn({}), null),
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useStorePayoutConfigQuery = (storeid: number | null) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/payout/config` });
  return useQuery({
    queryKey: adminStoreKeys.payoutConfig(storeid),
    queryFn: async (): Promise<StorePayoutConfig | null> =>
      unwrap<{ payout_config: StorePayoutConfig | null }>(await queryFn({}), { payout_config: null }).payout_config,
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useStorePayoutsQuery = (storeid: number | null) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/payouts` });
  return useQuery({
    queryKey: adminStoreKeys.payouts(storeid),
    queryFn: async (): Promise<StorePayout[]> =>
      unwrap<{ payouts: StorePayout[] }>(await queryFn({}), { payouts: [] }).payouts ?? [],
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useStoreSettingsQuery = (storeid: number | null) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/settings` });
  return useQuery({
    queryKey: adminStoreKeys.settings(storeid),
    queryFn: async (): Promise<StoreSettingsRow | null> =>
      unwrap<{ settings: StoreSettingsRow | null }>(await queryFn({}), { settings: null }).settings,
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useStoreAnalyticsQuery = (storeid: number | null) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/analytics` });
  return useQuery({
    queryKey: adminStoreKeys.analytics(storeid),
    queryFn: async (): Promise<StoreAnalytics | null> => unwrap(await queryFn({}), null),
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useStoreAuditQuery = (storeid: number | null) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/audit` });
  return useQuery({
    queryKey: adminStoreKeys.audit(storeid),
    queryFn: async (): Promise<StoreAuditEntry[]> =>
      unwrap<{ actions: StoreAuditEntry[] }>(await queryFn({}), { actions: [] }).actions ?? [],
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

const withoutEmpty = (params: StorePageParams) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));

/** `GET /<id>/listings` — every listing the store carries, drafts included. */
export const useStoreListingsQuery = (storeid: number | null, params: StorePageParams = {}) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/listings`, defaultErrorMessage: 'Failed to load listings' });
  return useQuery({
    queryKey: adminStoreKeys.listings(storeid, params),
    queryFn: async (): Promise<StoreListingsResponse> =>
      unwrap<StoreListingsResponse>(await queryFn(withoutEmpty(params)), {
        entities: [],
        total: 0,
        limit: 0,
        offset: 0,
        has_more: false,
      }),
    enabled: storeid !== null,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** `GET /<id>/permissions/users` — staff with per-code override rows. */
export const useStoreStaffQuery = (storeid: number | null, enabled = true) => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/${storeid}/permissions/users`, defaultErrorMessage: 'Failed to load staff' });
  return useQuery({
    queryKey: adminStoreKeys.staff(storeid),
    queryFn: async (): Promise<StoreStaffResponse> =>
      unwrap<StoreStaffResponse>(await queryFn({}), { storeid: storeid ?? 0, users: [] }),
    enabled: storeid !== null && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
